import * as React from 'react';
import {
  GDSCustomizableComponent,
  Box,
  styled,
  focusOutlineOuter,
  VisuallyHidden,
  GDSBackgroundColor,
} from '@leagueplatform/genesis-core';
import { formatClassName } from '../../utils/format-className.util';

const DISPLAY_NAME = 'Table';

const backgroundColorMap = {
  primary: '$surfaceBackgroundPrimary',
  secondary: '$surfaceBackgroundSecondary',
  tertiary: '$surfaceBackgroundTertiary',
};

const TableHeadBase = styled('thead', {});

interface GDSTableHeadProps
  extends GDSCustomizableComponent,
    React.HTMLAttributes<HTMLTableSectionElement> {
  children: React.ReactNode;
}

function TableHead({ children, className, ...props }: GDSTableHeadProps) {
  return (
    <TableHeadBase
      className={formatClassName('GDS-table-row', className)}
      {...props}
    >
      {children}
    </TableHeadBase>
  );
}

const TableRowBase = styled('tr', {});

interface GDSTableRowProps
  extends GDSCustomizableComponent,
    React.HTMLAttributes<HTMLTableRowElement> {
  children: React.ReactNode;
}

function TableRow({ children, className, ...props }: GDSTableRowProps) {
  return (
    <TableRowBase
      className={formatClassName('GDS-table-row', className)}
      {...props}
    >
      {children}
    </TableRowBase>
  );
}

const TableBodyBase = styled('tbody', {
  '& tr:nth-child(1) td': {
    borderBlockStart: 'none',
  },
});

interface GDSTableBodyProps
  extends GDSCustomizableComponent,
    React.HTMLAttributes<HTMLTableSectionElement> {
  children: React.ReactNode;
}
function TableBody({ children, className, ...props }: GDSTableBodyProps) {
  return (
    <TableBodyBase
      className={formatClassName('GDS-table-body', className)}
      {...props}
    >
      {children}
    </TableBodyBase>
  );
}

const TableCellBase = styled('td', {
  displayName: `${DISPLAY_NAME}Cell`,
  padding: '$one',
  borderBlockStartColor: '$onSurfaceBorderSubdued',
  borderBlockStartWidth: '$thin',
  borderBlockStartStyle: 'solid',
  typography: '$bodyOne',
  color: '$onSurfaceTextPrimary',
});

interface GDSTableCellProps
  extends GDSCustomizableComponent,
    React.HTMLAttributes<HTMLTableCellElement> {
  backgroundColor?: GDSBackgroundColor;
  width?: string;
}

function TableCell({
  backgroundColor: backgroundColorProp = 'primary',
  className,
  ...props
}: GDSTableCellProps) {
  const backgroundColor = backgroundColorMap[backgroundColorProp];
  return (
    <TableCellBase
      className={formatClassName('GDS-table-cell', className)}
      css={{ backgroundColor }}
      {...props}
    />
  );
}

interface GDSTableHeaderProps
  extends GDSCustomizableComponent,
    React.HTMLAttributes<HTMLTableCellElement> {
  backgroundColor?: GDSBackgroundColor;
  hideLabel?: boolean;
  label: string;
  scope?: 'col' | 'row' | 'rowgroup' | 'colgroup';
}

const TableHeaderCellBase = styled('th', {
  textAlign: 'start',
  padding: '$one',
  typography: '$subtitleTwo',
});

function TableHeader({
  label,
  hideLabel,
  backgroundColor: backgroundColorProp = 'secondary',
  css,
  scope = 'col',
  className,
  ...props
}: GDSTableHeaderProps) {
  const backgroundColor = backgroundColorMap[backgroundColorProp];
  const cellContent = hideLabel ? (
    <VisuallyHidden>{label}</VisuallyHidden>
  ) : (
    label
  );

  return (
    <TableHeaderCellBase
      scope={scope}
      css={{ backgroundColor, ...css }}
      className={formatClassName('GDS-table-header', className)}
      {...props}
    >
      {cellContent}
    </TableHeaderCellBase>
  );
}

const borderStyles = {
  borderColor: '$onSurfaceBorderSubdued',
  borderRadius: '$small',
  borderStyle: 'solid',
  borderWidth: '$thin',
};

const TableBase = styled('table', {});

interface TableWrapperProps {
  tableName: string;
  border?: boolean;
  isResponsive?: boolean;
  children?: React.ReactNode;
}

const TableWrapper = React.forwardRef<HTMLDivElement, TableWrapperProps>(
  ({ tableName, border, isResponsive, children }, ref) => {
    const tableWrapperClass = 'GDS-table-wrapper';

    if (!isResponsive) {
      return (
        <Box
          className={tableWrapperClass}
          ref={ref}
          css={{ ...(border && borderStyles), width: '100%' }}
        >
          {children}
        </Box>
      );
    }

    return (
      <Box
        // This has a tabIndex so that the table can be scrolled for keyboard navigation
        as="section"
        aria-label={tableName}
        tabIndex={0}
        className={tableWrapperClass}
        ref={ref}
        css={{
          overflowX: 'auto',
          width: '100%',
          '&[class*="GDS"]:focus': {
            outline: 'initial',
          },
          '&[class*="GDS"]:focus-visible': {
            ...focusOutlineOuter,
          },
          ...(border && borderStyles),
        }}
      >
        {children}
      </Box>
    );
  },
);

interface GDSTableProps
  extends GDSCustomizableComponent,
    React.HTMLAttributes<HTMLTableElement> {
  tableName: string;
  border?: boolean;
  isResponsive?: boolean;
  forwardedRef?: React.Ref<HTMLDivElement>;
}

function Table({
  border,
  className,
  children,
  css,
  isResponsive = true,
  tableName,
  forwardedRef,
  ...props
}: GDSTableProps) {
  return (
    <TableWrapper
      tableName={tableName}
      border={border}
      isResponsive={isResponsive}
      ref={forwardedRef}
    >
      <TableBase
        aria-label={tableName}
        css={{ borderCollapse: 'collapse', width: '100%', ...css }}
        className={formatClassName('GDS-table', className)}
        {...props}
      >
        {children}
      </TableBase>
    </TableWrapper>
  );
}

Table.displayName = DISPLAY_NAME;

Table.Head = TableHead;
Table.Row = TableRow;
Table.Body = TableBody;
Table.Cell = TableCell;
Table.Header = TableHeader;

// Remove previous exports
export { Table };
